import routes from "@gik/core/routes";
import { useBemCN } from "@gik/core/utils/bemBlock";
import { isVideo } from "@gik/core/utils/fileExtensions";
import { navigateTo } from "@gik/core/utils/navigateTo";
import { isEmpty } from "@gik/core/utils/validator";
import withComponentErrorBoundary from "@gik/core/utils/withComponentErrorBoundary";
import { CreateClickedLocation } from "@gik/create/enums/CreateAnalyticsEnums";
import { useCreateAnalyticsController } from "@gik/create/hooks/useCreateAnalyticsController";
import { Button } from "@gik/ui-legacy/Button";
import { Layout } from "@gik/ui-legacy/gik/Layout/Layout";
import { PageSection } from "@gik/ui-legacy/gik/PageSection";
import type React from "react";
import { LandingPageHeroCard } from "../components/LandingPageHeroCard";
import type { LandingPageHero } from "../types/LandingPageHero";

export interface IHeroSectionProps {
	hero: LandingPageHero;
	backgroundColor?: string;
	backgroundVideo?: string;
	backgroundImage?: string;
	backgroundImageMd?: string;
	ctaLabel?: string;
	ctaHref?: string;
	onCtaClick?: () => void;
}

/**
 * https://www.figma.com/design/FT2ninjhNiQOXfnOTaZfqy/%F0%9F%91%A8%F0%9F%8F%BC%E2%80%8D%F0%9F%8E%A4-Jade's-workspace-of-asset-creation?node-id=4696-8681&m=dev
 */
function LandingPageHeroSectionComp({
	hero,
	backgroundColor,
	backgroundVideo,
	backgroundImage,
	backgroundImageMd = backgroundImage,
	ctaLabel = "Start a page",
	ctaHref = routes.create(),
	onCtaClick,
}: React.PropsWithChildren<IHeroSectionProps>) {
	const bem = useBemCN("landing-page-hero-section");
	const { trackCreateClicked } = useCreateAnalyticsController();

	function onCreateClicked(e: React.MouseEvent) {
		e.preventDefault();
		if (onCtaClick) {
			onCtaClick();
		} else {
			trackCreateClicked(CreateClickedLocation.LANDINGPAGE_TOP);
		}
		navigateTo(ctaHref);
	}

	return (
		<>
			<style>
				{`
        .${bem().className} {
          --background: url(${backgroundImage});
          --background-md: url(${backgroundImageMd});
        }
      `}
			</style>
			<div
				{...bem(null, null, ["tw-relative", "tw-overflow-hidden"])}
				style={
					!isEmpty(backgroundColor)
						? {
								background: backgroundColor,
							}
						: {}
				}
			>
				{isVideo(backgroundVideo) && (
					<video
						autoPlay
						muted
						loop
						{...bem("background", null, ["tw-absolute"])}
					>
						<source src={backgroundVideo} />
					</video>
				)}
				<PageSection
					{...bem("header", [{ background: !isEmpty(backgroundImage) }])}
					asElement={"header"}
				>
					<LandingPageHeroCard hero={hero}>
						<Layout direction={"column"} align={"center"} gap={8}>
							<Layout gap={4}>
								<Button
									type={"a"}
									href={ctaHref}
									onClick={onCreateClicked}
									className={"tw-flex-1"}
								>
									{ctaLabel}
								</Button>
							</Layout>
						</Layout>
					</LandingPageHeroCard>
				</PageSection>
			</div>
		</>
	);
}

export const LandingPageHeroSection = withComponentErrorBoundary(
	LandingPageHeroSectionComp,
);
